import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useTranslate } from "src/locales";

interface EditOperationModalProps {
  open: boolean;
  onClose: () => void;
  operationData: {
    id: number;
    name: string;
    note: string;
    quantity: number;
  };
  patientId: string;
  onSave: (updatedData: { id: number; note: string; quantity: number }) => void;
}

const EditOperationModal: React.FC<EditOperationModalProps> = ({
  open,
  onClose,
  operationData,
  patientId,
  onSave,
}) => {
  const { t } = useTranslate();
  const [note, setNote] = useState(operationData.note);
  const [quantity, setQuantity] = useState(operationData.quantity.toString());

  const handleSave = () => {
    const updatedData = {
      id: operationData.id,
      note: note,
      quantity: parseInt(quantity) || 1,
    };
    onSave(updatedData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "60vh",
          maxHeight: "70vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          p: 1,
          px: 3,
          mt: 5,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("patient-request-service.edit-operation-modal.title")}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pb: 1 }}>
        <Box sx={{ p: 2 }}>
          <PatientDatas
            patientId={patientId}
            isOpenMedCard={true}
            isCreateMedCard={true}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid #000000",
            mx: "2%",
          }}
        >
          <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
            <ByzMuiSaveButton onClick={handleSave} />
          </Box>
          <Box sx={{ transform: "scaleX(0.8) scaleY(0.9)" }}>
            <ByzMuiCloseButton onClick={onClose} />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", mb: 2 }}>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
                width: "50%",
                pl: 1,
                backgroundColor: "#F4F7FE",
                px: 1,
                py: 1.5,
              }}
            >
              {t("patient-request-service.edit-operation-modal.change")}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px", // TextField ile aynı yükseklik
                }}
              >
                {t("patient-request-service.edit-operation-modal.note")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                value={note}
                onChange={(e) => setNote(e.target.value)}
                size="small"
                placeholder={t(
                  "patient-request-service.edit-operation-modal.note-placeholder"
                )}
                sx={{
                  width: "50%",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#2498f3",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2498f3",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2498f3",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  borderBottom: "1px solid #e0e0e0",
                  pb: 1,
                  pl: "10%",
                  display: "flex",
                  alignItems: "center",
                  height: "40px", // TextField ile aynı yükseklik
                }}
              >
                {t("patient-request-service.edit-operation-modal.amount")}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                size="small"
                type="number"
                placeholder={t(
                  "patient-request-service.edit-operation-modal.enter"
                )}
                sx={{
                  width: "50%",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#2498f3",
                    },
                    "&:hover fieldset": {
                      borderColor: "#2498f3",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2498f3",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditOperationModal;
